import { useCallback, useMemo, useState } from "react";
import { WidgetTemplate, WidgetTemplateCategory } from "../types";
import { widgetTemplateEndpoints } from "../api/endpoints";

interface UseWidgetTemplatesReturn {
    templates: WidgetTemplate[];
    categories: WidgetTemplateCategory[];
    filteredTemplates: WidgetTemplate[];
    selectedCategory: string | null;
    searchQuery: string;
    selectedTemplate: WidgetTemplate | null;
    isLoading: boolean;
    error: string | null;
    setSelectedCategory: (slug: string | null) => void;
    setSearchQuery: (query: string) => void;
    setSelectedTemplate: (template: WidgetTemplate | null) => void;
    fetchTemplates: () => Promise<void>;
    reset: () => void;
}

export const useWidgetTemplates = (): UseWidgetTemplatesReturn => {
    const [templates, setTemplates] = useState<WidgetTemplate[]>([]);
    const [categories, setCategories] = useState<WidgetTemplateCategory[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState<WidgetTemplate | null>(null);

    const filteredTemplates = useMemo(() => {
        let result = templates;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter((t) => t.name.toLowerCase().includes(query) || t.description.toLowerCase().includes(query) || t.category.name.toLowerCase().includes(query));
        } else if (selectedCategory) {
            result = result.filter((t) => t.category.slug === selectedCategory);
        }

        return result.sort((a, b) => a.sortOrder - b.sortOrder);
    }, [templates, selectedCategory, searchQuery]);

    const fetchTemplates = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await widgetTemplateEndpoints.getTemplates();
            setTemplates(data.templates);
            setCategories(data.categories);
            // Auto-select first category
            if (data.categories.length > 0) {
                setSelectedCategory(data.categories[0].slug);
            }
        } catch (err) {
            setError("Failed to load templates. Please try again.");
            console.error("Error fetching templates:", err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const reset = useCallback(() => {
        setSelectedCategory(null);
        setSearchQuery("");
        setSelectedTemplate(null);
    }, []);

    return {
        templates,
        categories,
        filteredTemplates,
        selectedCategory,
        searchQuery,
        selectedTemplate,
        isLoading,
        error,
        setSelectedCategory,
        setSearchQuery,
        setSelectedTemplate,
        fetchTemplates,
        reset,
    };
};
